import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  NAV_REGISTRY,
  type NavTreeDelta,
  type NavTreeDeltaItem,
  type NavTreeDeltaSection,
  useTenantNavConfig,
  useUserNavTree,
} from '@sazeo/sdk';
import type { SupabaseClientLike } from '@sazeo/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@sazeo/ui';
import { Eye, EyeOff, GripVertical, Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Scope = 'user' | 'tenant';

export interface NavCustomizerDrawerProps {
  scope: Scope;
  open: boolean;
  onClose: () => void;
  supabase: SupabaseClientLike;
  tenantId: string;
}

const EMPTY_DELTA: NavTreeDelta = { version: 1, sections: [] };

function cloneDelta(delta: NavTreeDelta | null): NavTreeDelta {
  return JSON.parse(JSON.stringify(delta ?? EMPTY_DELTA)) as NavTreeDelta;
}

function toLegacyNavKey(key: string): string {
  if (key.startsWith('nav.sections.')) return key.replace('nav.sections.', 'nav_sections_');
  if (key.startsWith('nav.')) return key.replace('nav.', 'nav_');
  return key;
}

function getSectionLabel(section: NavTreeDeltaSection, t: (key: string) => string): string {
  if (section.label) return section.label;
  const registry = NAV_REGISTRY.find((s) => s.id === section.id);
  if (!registry) return section.id;
  const translated = t(toLegacyNavKey(registry.label_key));
  return translated === toLegacyNavKey(registry.label_key) ? section.id : translated;
}

function getItemLabel(
  item: NavTreeDeltaItem,
  sectionId: string,
  t: (key: string) => string,
): string {
  if (item.label) return item.label;
  const registrySection = NAV_REGISTRY.find((s) => s.id === sectionId);
  const registryItem = registrySection?.items.find((i) => i.id === item.id);
  if (!registryItem) return item.id;
  const translated = t(toLegacyNavKey(registryItem.label_key));
  return translated === toLegacyNavKey(registryItem.label_key) ? item.id : translated;
}

function ensureSection(delta: NavTreeDelta, sectionId: string): NavTreeDeltaSection {
  let section = delta.sections.find((s) => s.id === sectionId);
  if (!section) {
    section = { id: sectionId, items: [] };
    delta.sections.push(section);
  }
  if (!section.items) section.items = [];
  return section;
}

function ensureRegistryDelta(delta: NavTreeDelta): NavTreeDelta {
  const next = cloneDelta(delta);
  for (const registrySection of NAV_REGISTRY) {
    const section = ensureSection(next, registrySection.id);
    if (!section.items) {
      section.items = [];
    }
    const items = section.items;
    const ids = new Set((section.items ?? []).map((i) => i.id));
    for (const item of registrySection.items) {
      if (!ids.has(item.id)) {
        items.push({ id: item.id });
      }
    }
  }
  return next;
}

// ---------------------------------------------------------------------------
// Sortable item row
// ---------------------------------------------------------------------------

interface SortableItemRowProps {
  item: NavTreeDeltaItem;
  sectionId: string;
  onToggleHidden: () => void;
  t: (key: string) => string;
}

function SortableItemRow({ item, sectionId, onToggleHidden, t }: SortableItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px]',
        item.hidden && 'opacity-50',
        isDragging && 'z-10 bg-[var(--color-surface-2)] shadow-sm',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-[var(--color-text-tertiary)] active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical size={14} />
      </button>
      <span className="flex-1 truncate">{getItemLabel(item, sectionId, t)}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onToggleHidden}
        aria-label={item.hidden ? t('nav_customizer_show') : t('nav_customizer_hide')}
      >
        {item.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
      </Button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Sortable section card
// ---------------------------------------------------------------------------

interface SortableSectionCardProps {
  section: NavTreeDeltaSection;
  scope: Scope;
  onToggleHidden: () => void;
  onLabelChange: (label: string) => void;
  onItemToggleHidden: (itemId: string) => void;
  onItemDragEnd: (event: DragEndEvent) => void;
  onAddExternal: () => void;
  t: (key: string) => string;
}

function SortableSectionCard({
  section,
  scope,
  onToggleHidden,
  onLabelChange,
  onItemToggleHidden,
  onItemDragEnd,
  onAddExternal,
  t,
}: SortableSectionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const itemIds = (section.items ?? []).map((i) => i.id);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface)]',
        isDragging && 'shadow-lg',
      )}
    >
      {/* Section header */}
      <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <button
          type="button"
          className="cursor-grab touch-none text-[var(--color-text-tertiary)] active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label="Drag section to reorder"
        >
          <GripVertical size={14} />
        </button>

        {scope === 'tenant' && section.id.startsWith('custom-') ? (
          <Input
            value={section.label ?? ''}
            onChange={(e) => onLabelChange(e.target.value)}
            className="h-7 text-[13px]"
          />
        ) : (
          <span className="text-[13px] font-medium">{getSectionLabel(section, t)}</span>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={onToggleHidden}
          aria-label={section.hidden ? t('nav_customizer_show') : t('nav_customizer_hide')}
        >
          {section.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
        </Button>
      </div>

      {/* Items */}
      <DndContext
        sensors={itemSensors}
        collisionDetection={closestCenter}
        onDragEnd={onItemDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5 px-2 py-2">
            {(section.items ?? []).map((item) => (
              <SortableItemRow
                key={item.id}
                item={item}
                sectionId={section.id}
                onToggleHidden={() => onItemToggleHidden(item.id)}
                t={t}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {/* Add external link */}
      <div className="border-t border-[var(--color-border)] p-2">
        <Button type="button" variant="ghost" size="sm" onClick={onAddExternal}>
          <Plus size={14} className="mr-1.5" />
          {t('nav_customizer_add_item')}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------

export function NavCustomizerDrawer({
  scope,
  open,
  onClose,
  supabase,
  tenantId,
}: NavCustomizerDrawerProps) {
  const { t } = useTranslation('core.nav');
  const tenantNav = useTenantNavConfig(supabase, tenantId);
  const [userNav, setUserNav] = useUserNavTree(supabase);
  const source = scope === 'tenant' ? tenantNav.data : userNav;

  const [draft, setDraft] = useState<NavTreeDelta>(EMPTY_DELTA);
  const [saving, setSaving] = useState(false);
  const lastSavedRef = useRef('');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [addSectionLabel, setAddSectionLabel] = useState('');
  const [addExternalOpen, setAddExternalOpen] = useState<string | null>(null);
  const [addExternalLabel, setAddExternalLabel] = useState('');
  const [addExternalUrl, setAddExternalUrl] = useState('https://');

  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!open) return;
    const next = ensureRegistryDelta(cloneDelta(source));
    setDraft(next);
    lastSavedRef.current = JSON.stringify(next);
  }, [open, source]);

  const saveDraft = useMemo(
    () => async (value: NavTreeDelta) => {
      setSaving(true);
      try {
        if (scope === 'tenant') {
          await tenantNav.save(value);
        } else {
          setUserNav(value);
        }
      } finally {
        setSaving(false);
      }
    },
    [scope, tenantNav, setUserNav],
  );

  useEffect(() => {
    if (!open) return;
    const current = JSON.stringify(draft);
    if (current === lastSavedRef.current) return;
    const timer = setTimeout(() => {
      void saveDraft(draft);
      lastSavedRef.current = current;
    }, 500);
    return () => clearTimeout(timer);
  }, [draft, open, saveDraft]);

  // Section drag end
  function handleSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      const ids = prev.sections.map((s) => s.id);
      const oldIdx = ids.indexOf(active.id as string);
      const newIdx = ids.indexOf(over.id as string);
      return { ...prev, sections: arrayMove(prev.sections, oldIdx, newIdx) };
    });
  }

  // Item drag end within a section
  function handleItemDragEnd(sectionId: string, event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      const next = cloneDelta(prev);
      const section = next.sections.find((s) => s.id === sectionId);
      if (!section?.items) return prev;
      const ids = section.items.map((i) => i.id);
      const oldIdx = ids.indexOf(active.id as string);
      const newIdx = ids.indexOf(over.id as string);
      section.items = arrayMove(section.items, oldIdx, newIdx);
      return next;
    });
  }

  function setSectionHidden(sectionId: string, hidden: boolean) {
    setDraft((prev) => {
      const next = ensureRegistryDelta(prev);
      const section = ensureSection(next, sectionId);
      section.hidden = hidden;
      return { ...next };
    });
  }

  function setItemHidden(sectionId: string, itemId: string, hidden: boolean) {
    setDraft((prev) => {
      const next = ensureRegistryDelta(prev);
      const section = ensureSection(next, sectionId);
      const items = section.items ?? [];
      const idx = items.findIndex((i) => i.id === itemId);
      if (idx >= 0) {
        const current = items[idx];
        if (current) items[idx] = { ...current, hidden };
      } else {
        items.push({ id: itemId, hidden });
      }
      section.items = items;
      return { ...next };
    });
  }

  function setSectionLabel(sectionId: string, label: string) {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, label } : s)),
    }));
  }

  function commitAddSection() {
    const label = addSectionLabel.trim();
    if (!label) return;
    const id = `custom-${Date.now()}`;
    setDraft((prev) => ({
      ...prev,
      sections: [...prev.sections, { id, label, items: [] }],
    }));
    setAddSectionOpen(false);
    setAddSectionLabel('');
  }

  function commitAddExternal(sectionId: string) {
    const label = addExternalLabel.trim();
    const route = addExternalUrl.trim();
    if (!label || !route) return;
    setDraft((prev) => {
      const next = ensureRegistryDelta(prev);
      const section = ensureSection(next, sectionId);
      section.items = [
        ...(section.items ?? []),
        { id: `custom-link-${Date.now()}`, label, route, icon: 'ExternalLink', external: true },
      ];
      return { ...next };
    });
    setAddExternalOpen(null);
    setAddExternalLabel('');
    setAddExternalUrl('https://');
  }

  async function resetToDefault() {
    if (scope === 'tenant') {
      await tenantNav.save(EMPTY_DELTA);
    } else {
      setUserNav(null);
    }
    setDraft(ensureRegistryDelta(EMPTY_DELTA));
    setResetConfirmOpen(false);
  }

  const sectionIds = draft.sections.map((s) => s.id);

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent
        className={cn(
          'left-auto right-0 top-0 h-full w-full max-w-[480px]',
          'translate-x-0 translate-y-0 rounded-none border-l p-0',
        )}
      >
        <DialogHeader className="border-b border-[var(--color-border)] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <DialogTitle>{t('nav_customizer_title')}</DialogTitle>
              <DialogDescription>
                {scope === 'tenant' ? t('nav_customizer_for_company') : t('nav_customizer_for_me')}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {saving && (
                <span className="text-[11px] text-[var(--color-text-tertiary)]">
                  {t('nav_customizer_saved')}
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setResetConfirmOpen(true)}
              >
                <RotateCcw size={14} className="mr-1.5" />
                {t('nav_customizer_reset')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={14} />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[calc(100vh-72px)] overflow-y-auto p-4">
          <DndContext
            sensors={sectionSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {draft.sections.map((section) => (
                  <SortableSectionCard
                    key={section.id}
                    section={section}
                    scope={scope}
                    onToggleHidden={() => setSectionHidden(section.id, !section.hidden)}
                    onLabelChange={(label) => setSectionLabel(section.id, label)}
                    onItemToggleHidden={(itemId) =>
                      setItemHidden(
                        section.id,
                        itemId,
                        !section.items?.find((i) => i.id === itemId)?.hidden,
                      )
                    }
                    onItemDragEnd={(e) => handleItemDragEnd(section.id, e)}
                    onAddExternal={() => {
                      setAddExternalLabel('');
                      setAddExternalUrl('https://');
                      setAddExternalOpen(section.id);
                    }}
                    t={t}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {scope === 'tenant' && (
            <div className="mt-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setAddSectionLabel('');
                  setAddSectionOpen(true);
                }}
              >
                <Plus size={14} className="mr-1.5" />
                {t('nav_customizer_add_section')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Reset confirm */}
      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('nav_customizer_reset')}</DialogTitle>
            <DialogDescription>{t('nav_customizer_reset_confirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setResetConfirmOpen(false)}
            >
              {t('cancel', { ns: 'core.settings', defaultValue: 'Cancel' })}
            </Button>
            <Button type="button" size="sm" onClick={() => void resetToDefault()}>
              {t('nav_customizer_reset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add section */}
      <Dialog open={addSectionOpen} onOpenChange={setAddSectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('nav_customizer_add_section')}</DialogTitle>
          </DialogHeader>
          <Input
            value={addSectionLabel}
            onChange={(e) => setAddSectionLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitAddSection();
            }}
            autoFocus
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAddSectionOpen(false)}
            >
              {t('cancel', { ns: 'core.settings', defaultValue: 'Cancel' })}
            </Button>
            <Button type="button" size="sm" onClick={commitAddSection}>
              {t('nav_customizer_add_section')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add external link */}
      <Dialog
        open={addExternalOpen !== null}
        onOpenChange={(v) => {
          if (!v) setAddExternalOpen(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('nav_customizer_external_link')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Input
              placeholder={t('nav_customizer_external_link')}
              value={addExternalLabel}
              onChange={(e) => setAddExternalLabel(e.target.value)}
              autoFocus
            />
            <Input
              placeholder="https://"
              value={addExternalUrl}
              onChange={(e) => setAddExternalUrl(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAddExternalOpen(null)}
            >
              {t('cancel', { ns: 'core.settings', defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (addExternalOpen) commitAddExternal(addExternalOpen);
              }}
            >
              {t('nav_customizer_add_item')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
